import { get, send } from "../api.js";

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  targetPath: string;
  contextType?: string | null;
  contextId?: string | null;
  createdAt: string;
  readAt: string | null;
  actorName: string | null;
}

export interface NotificationSummary {
  unread: number;
  recent: NotificationItem[];
}

export interface NotificationPage {
  items: NotificationItem[];
  page: number;
  pages: number;
  total: number;
}

export const notificationApi = {
  /** The badge and the panel, in one request, because the shell wants both. */
  summary: () => get<NotificationSummary>("/notifications/summary"),

  list: (filter: "all" | "unread", page: number) =>
    get<NotificationPage>("/notifications", {
      filter,
      page: String(page),
      pageSize: "25",
    }),

  markRead: (id: string) =>
    send<{ readAt: string | null }>(
      "PATCH",
      `/notifications/${encodeURIComponent(id)}/read`,
    ),

  markAllRead: () => send<{ marked: number }>("PATCH", "/notifications/read-all"),
};

/**
 * The one thing a notification's type decides on screen.
 *
 * A glyph rather than an icon set: the panel is a list of sentences and the
 * mark is there to let the eye sort them, not to carry meaning of its own —
 * every row says in words what happened.
 */
export function notificationGlyph(type: string): string {
  if (type.startsWith("message")) return "✉";
  if (type.startsWith("announcement")) return "📣";
  if (type.startsWith("group_activity")) return "🎼";
  if (type.startsWith("waitlist")) return "⏳";
  if (type.startsWith("appointment")) return "📅";
  if (type.startsWith("lesson_request")) return "📋";
  if (type.startsWith("practice")) return "🎵";
  if (type.startsWith("resource")) return "📎";
  if (type.startsWith("student")) return "👤";
  return "•";
}

/** Resolve a known offer context without trusting arbitrary notification URLs. */
export function notificationTargetPath(item: NotificationItem): string {
  const target = item.targetPath;
  const waitlist = item.type === "waitlist.offer";
  const offerId = item.contextId;
  const validOfferId = item.contextType === "waitlist_offer" && typeof offerId === "string"
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(offerId);
  try {
    if (!target.startsWith("/") || target.startsWith("//")) throw new Error("Invalid notification destination");
    const url = new URL(target, "https://hub.invalid");
    if (url.origin !== "https://hub.invalid") throw new Error("Invalid notification destination");
    if (waitlist && !validOfferId) {
      if (url.pathname !== "/home") return "/notifications";
      const studentId = url.searchParams.get("student");
      if (!studentId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(studentId)) return "/notifications";
      url.searchParams.set("offer_context", "unavailable");
      url.searchParams.delete("offer");
      return `${url.pathname}${url.search}${url.hash}`;
    }
    if (!waitlist || !validOfferId) return `${url.pathname}${url.search}${url.hash}`;
    const studentId = url.searchParams.get("student");
    if (
      url.pathname !== "/home" ||
      !studentId ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(studentId)
    ) return "/notifications";
    url.searchParams.set("offer", offerId!);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return "/notifications";
  }
}
